// Typed DOM helpers: element selection + a small declarative element factory.

export const $ = <T extends HTMLElement>(sel: string): T => {
  const node = document.querySelector<T>(sel);
  if (!node) throw new Error(`Element not found: ${sel}`);
  return node;
};

// Tags actually used by the view.
type ElementTag = "div" | "span" | "button";

interface Props {
  readonly class?: string;
  readonly dataset?: Readonly<Record<string, string>>;
  readonly on?: Readonly<Record<string, (e: Event) => void>>;
}

// First argument is either a bare class name or a full props object.
type ClassOrProps = string | Props;

const toProps = (classOrProps: ClassOrProps): Props =>
  typeof classOrProps === "string" ? { class: classOrProps } : classOrProps;

// Curried factory: `el(tag)` yields a builder. `children` is either child
// nodes or a string; a string makes a text leaf, so only leaves carry text.
// Event listeners are wired here, from the `on` prop.
const el =
  (tag: ElementTag) =>
  (
    classOrProps: ClassOrProps,
    children: readonly Node[] | string = [],
  ): Node => {
    const node = document.createElement(tag);
    const props = toProps(classOrProps);

    if (props.class) node.className = props.class;
    if (props.dataset) {
      Object.entries(props.dataset).forEach(([k, v]) => (node.dataset[k] = v));
    }
    if (props.on) {
      Object.entries(props.on).forEach(([type, handler]) =>
        node.addEventListener(type, handler),
      );
    }

    if (typeof children === "string") {
      node.textContent = children;
    } else {
      children.forEach((c) => node.appendChild(c));
    }

    return node;
  };

// One helper per tag used.
export const DIV = el("div");
export const SPAN = el("span");
export const BUTTON = el("button");
